import React, { useMemo, useState, useEffect } from 'react';
import { FaCopy, FaCheck, FaXmark } from 'react-icons/fa6';
import * as copyController from '../controllers/copyController';
import { parseGenericSections } from '../utils/parsePromptText';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { useAppContext } from '../../../../shared/context/AppContext';
import ContextVariableSelector from '../../../contextVariables/presentation/components/ContextVariableSelector';
import * as contextVariableService from '../../../contextVariables/data/services/contextVariableService';
import './PromptDetails.css';

const PLACEHOLDER_COLORS = [
  '#7EC9FF',
  '#47D59D',
  '#FEC031',
  '#FF613E',
  '#C084FC',
  '#60A5FA',
  '#F472B6',
  '#34D399',
  '#F97316',
];

const PromptContent = ({ promptText, promptId = null, promptTitle = null, isGlobal = false, selectedContextVariableIds = [], onContextVariableSelectionChange }) => {
  const [copied, setCopied] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [formError, setFormError] = useState(null);
  const [selectedContextVariables, setSelectedContextVariables] = useState([]);
  const [localSelectedIds, setLocalSelectedIds] = useState(selectedContextVariableIds || []);
  const { trackEvent } = useAnalytics();
  const { organization } = useAppContext();

  const { segments, placeholderInstances } = useMemo(() => {
    const pattern = /#([A-Za-z0-9_]+)/g;
    const newSegments = [];
    const newInstances = [];
    const placeholderCounts = {};
    let colorIndex = 0;
    let match;
    let lastIndex = 0;

    const getNextColor = () => {
      const color = PLACEHOLDER_COLORS[colorIndex % PLACEHOLDER_COLORS.length];
      colorIndex += 1;
      return color;
    };

    while ((match = pattern.exec(promptText || '')) !== null) {
      if (match.index > lastIndex) {
        newSegments.push({
          type: 'text',
          value: (promptText || '').slice(lastIndex, match.index),
        });
      }

      const label = match[1];
      const occurrence = (placeholderCounts[label] || 0) + 1;
      placeholderCounts[label] = occurrence;

      const id = `${label}-${occurrence}`;
      const color = getNextColor();

      const placeholderToken = {
        type: 'placeholder',
        id,
        label,
        token: match[0],
        occurrence,
        color,
        totalOccurrences: placeholderCounts[label],
      };

      newSegments.push(placeholderToken);
      newInstances.push(placeholderToken);

      lastIndex = pattern.lastIndex;
    }

    if ((promptText || '').length > lastIndex) {
      newSegments.push({
        type: 'text',
        value: (promptText || '').slice(lastIndex),
      });
    }

    return {
      segments: newSegments,
      placeholderInstances: newInstances,
    };
  }, [promptText]);

  const labelCounts = useMemo(() => {
    return placeholderInstances.reduce((acc, instance) => {
      acc[instance.label] = (acc[instance.label] || 0) + 1;
      return acc;
    }, {});
  }, [placeholderInstances]);

  // Parse promptText into sections (discovered dynamically from "Label: "
  // prefixes, not a fixed field set) and process placeholders within each.
  const parsedSectionsWithContent = useMemo(() => {
    if (!promptText) return [];

    const parsed = parseGenericSections(promptText);
    const sections = [];
    let globalPlaceholderIndex = 0;

    parsed.forEach(({ key, title, content }) => {
      if (content && content.trim()) {
        // Process placeholders within this section's content
        const pattern = /#([A-Za-z0-9_]+)/g;
        const contentParts = [];
        let lastIndex = 0;
        let match;

        while ((match = pattern.exec(content)) !== null) {
          if (match.index > lastIndex) {
            contentParts.push({
              type: 'text',
              value: content.slice(lastIndex, match.index),
            });
          }

          // Match to the next global placeholder instance
          if (globalPlaceholderIndex < placeholderInstances.length) {
            const globalInstance = placeholderInstances[globalPlaceholderIndex];
            contentParts.push({
              type: 'placeholder',
              id: globalInstance.id,
              label: globalInstance.label,
              token: globalInstance.token,
              occurrence: globalInstance.occurrence,
              color: globalInstance.color,
            });
            globalPlaceholderIndex++;
          } else {
            // Fallback if not found (shouldn't happen)
            contentParts.push({
              type: 'placeholder',
              id: `${match[1]}-1`,
              label: match[1],
              token: match[0],
              occurrence: 1,
              color: PLACEHOLDER_COLORS[0],
            });
          }

          lastIndex = pattern.lastIndex;
        }

        if (content.length > lastIndex) {
          contentParts.push({
            type: 'text',
            value: content.slice(lastIndex),
          });
        }

        sections.push({ key, title, contentParts });
      }
    });

    return sections;
  }, [promptText, placeholderInstances]);

  useEffect(() => {
    setInputValues({});
    setShowOverlay(false);
    setFormError(null);
  }, [promptText]);

  useEffect(() => {
    setLocalSelectedIds(selectedContextVariableIds || []);
  }, [selectedContextVariableIds]);

  // Load selected context variables
  useEffect(() => {
    const loadContextVariables = async () => {
      if (localSelectedIds.length === 0) {
        setSelectedContextVariables([]);
        return;
      }

      try {
        const allVariables = await contextVariableService.getAllContextVariables();
        const selected = allVariables
          .filter(v => localSelectedIds.includes(v.id))
          .map(v => v.toJSON());
        setSelectedContextVariables(selected);
      } catch (error) {
        console.error('Error loading context variables:', error);
        setSelectedContextVariables([]);
      }
    };

    loadContextVariables();
  }, [localSelectedIds]);

  const handleContextVariableSelectionChange = (ids) => {
    setLocalSelectedIds(ids);
    if (onContextVariableSelectionChange) {
      onContextVariableSelectionChange(ids);
    }
  };

  const buildFinalPromptText = (baseText, inputValuesForPlaceholders = {}) => {
    // Build text with placeholders filled in
    let finalText = baseText;
    if (placeholderInstances.length > 0 && Object.keys(inputValuesForPlaceholders).length > 0) {
      finalText = segments
        .map((segment) => {
          if (segment.type === 'placeholder') {
            return inputValuesForPlaceholders[segment.id] ?? '';
          }
          return segment.value;
        })
        .join('');
    }

    // Add selected context variables to the Context section. Works generically
    // across whatever labels a prompt actually uses (Context/Task/... or
    // Context/Objective/Style/...) instead of a fixed field set, so sections
    // outside the legacy 6 aren't silently dropped when rebuilding.
    if (selectedContextVariables.length > 0) {
      const sections = parseGenericSections(finalText);
      const contextParts = selectedContextVariables.map((variable) => variable.context);
      let foundContext = false;

      const parts = sections.map((section) => {
        if (section.title === 'Context') {
          foundContext = true;
          const combined = [...contextParts, section.content].filter(Boolean).join('\n\n');
          return `Context: ${combined}`;
        }
        return section.title === 'Persona' ? section.content : `${section.title}: ${section.content}`;
      });

      if (!foundContext && contextParts.length > 0) {
        parts.splice(1, 0, `Context: ${contextParts.join('\n\n')}`);
      }

      finalText = parts.join('\n\n');
    }

    return finalText;
  };

  const handleCopy = async () => {
    if (!placeholderInstances.length) {
      const finalText = buildFinalPromptText(promptText);
      const orgId = organization?.id || organization?.orgId || null;
      const success = await copyController.copyPrompt(finalText, promptId, orgId);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        // Track copy event if promptId is available
        if (promptId) {
          trackEvent(EVENTS.PROMPT_COPIED, {
            promptId,
            promptTitle: promptTitle || null,
            isGlobal,
            source: 'prompt_details',
          });
        }
      }
      return;
    }

    setCopied(false);
    setInputValues((currentValues) => {
      if (Object.keys(currentValues).length) {
        return currentValues;
      }

      const initialValues = {};
      placeholderInstances.forEach((instance) => {
        initialValues[instance.id] = '';
      });
      return initialValues;
    });
    setFormError(null);
    setShowOverlay(true);
  };

  const handleOverlayCopy = async () => {
    const missingInputs = placeholderInstances.filter((instance) => !inputValues[instance.id]?.trim());

    if (missingInputs.length) {
      setFormError('Please fill in all placeholder values before copying.');
      return;
    }

    const finalText = buildFinalPromptText(promptText, inputValues);

    const orgId = organization?.id || organization?.orgId || null;
    const success = await copyController.copyPrompt(finalText, promptId, orgId);
    if (success) {
      setCopied(true);
      setShowOverlay(false);
      setTimeout(() => setCopied(false), 2000);
      // Track copy event if promptId is available
      if (promptId) {
        trackEvent(EVENTS.PROMPT_COPIED, {
          promptId,
          promptTitle: promptTitle || null,
          isGlobal,
          source: 'prompt_details',
        });
      }
    }
  };

  const handleOverlayClose = () => {
    setShowOverlay(false);
    setFormError(null);
  };

  const handleInputChange = (id, value) => {
    if (formError) {
      setFormError(null);
    }
    setInputValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <div className="prompt-content-section">
      <div className="prompt-text-container">
        <div className="prompt-text-header">
          <h2 className="prompt-text-title">Prompt text</h2>
          <button
            className={`copy-prompt-button-small ${copied ? 'copied' : ''}`}
            data-tutorial-target="copy-button"
            onClick={handleCopy}
            aria-label={copied ? 'Copied!' : 'Copy'}
          >
            {copied ? (
              <>
                <FaCheck className="copy-icon" />
                <span className="copy-button-text">Copied!</span>
              </>
            ) : (
              <FaCopy className="copy-icon" />
            )}
          </button>
        </div>
        <div className="prompt-text-body">
          <ContextVariableSelector
            selectedIds={localSelectedIds}
            onSelectionChange={handleContextVariableSelectionChange}
          />
          <div className="prompt-text">
            {parsedSectionsWithContent.map((section, sectionIndex) => {
              return (
                <div key={section.key} className="prompt-section">
                  <span className="prompt-section-title">{section.title}</span>
                  <div className="prompt-section-content">
                    {section.contentParts.map((part, partIndex) => {
                      if (part.type === 'placeholder') {
                        const totalForLabel = labelCounts[part.label] || 0;
                        const displayLabel =
                          totalForLabel > 1 ? `#${part.label} (${part.occurrence})` : `#${part.label}`;

                        return (
                          <span
                            key={`${part.id}-${partIndex}`}
                            className="prompt-placeholder-token"
                          >
                            {displayLabel}
                          </span>
                        );
                      }

                      return <span key={`${section.key}-${partIndex}`}>{part.value}</span>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {showOverlay && (
        <div className="placeholder-overlay" onClick={handleOverlayClose}>
          <div className="placeholder-sidebar" onClick={(event) => event.stopPropagation()}>
            <div className="placeholder-sidebar-header">
              <h3>Fill in your details</h3>
              <button className="placeholder-close-button" onClick={handleOverlayClose} aria-label="Close">
                <FaXmark />
              </button>
            </div>
            <div className="placeholder-sidebar-body">
              {placeholderInstances.map((instance) => {
                const totalForLabel = labelCounts[instance.label] || 0;
                const displayLabel =
                  totalForLabel > 1
                    ? `#${instance.label} (${instance.occurrence})`
                    : `#${instance.label}`;

                return (
                  <div className="placeholder-input-group" key={instance.id}>
                    <label className="placeholder-input-label" htmlFor={instance.id}>
                      <span className="placeholder-label-badge">
                        {displayLabel}
                      </span>
                    </label>
                    <textarea
                      id={instance.id}
                      className="placeholder-input"
                      value={inputValues[instance.id] ?? ''}
                      onChange={(event) => handleInputChange(instance.id, event.target.value)}
                      rows={3}
                    />
                  </div>
                );
              })}
            </div>
            {formError && <div className="placeholder-error">{formError}</div>}
            <div className="placeholder-sidebar-footer">
              <button className="placeholder-copy-button" onClick={handleOverlayCopy}>
                Copy prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptContent;

